import { PaymentProvider, SubscriptionStatus } from "@brookbush/database";
import { type SubscriptionExpanded, UserAction } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import { memo } from "react";
import toast from "react-hot-toast";
import { mutate as globalMutate } from "swr";

import { useSubscriptionDB, useUserAction } from "~/hooks";
import useCheckout from "~/hooks/useCheckout";
import usePayPalSubscription from "~/hooks/usePayPalSubscription";
import usePlanConfig from "~/hooks/usePlanConfig";
import type { BecomeAMemberSurface } from "~/lib/becomeAMemberTracking";
import captureBecomeAMemberEvent from "~/lib/captureBecomeAMemberEvent";

const BecomeAMemberCheckoutPayPalButton = () => {
	const { planConfig } = usePlanConfig();
	const { completeCheckout, setPaymentSubmitting, isPaymentSubmitting } =
		useCheckout();

	const { refreshSubscriptionDB } = useSubscriptionDB();

	const { paypalSubscription } = usePayPalSubscription();

	const { userId } = useAuth();
	const { userAction } = useUserAction();
	const surface: BecomeAMemberSurface =
		userAction === UserAction.BECOME_A_MEMBER ? "modal" : "page";

	const capturePlanProperties = {
		surface,
		interval: planConfig.price?.recurring?.interval,
		promo_code: planConfig.promoCode?.code,
		price_id: planConfig.price?.id,
	};

	const isChangingPlan =
		paypalSubscription?.status === SubscriptionStatus.ACTIVE &&
		paypalSubscription?.plan_id !== planConfig.price?.paypalPlanId;

	return (
		<PayPalScriptProvider
			options={{
				clientId: process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID!,
				components: "buttons",
				vault: true,
			}}
		>
			<PayPalButtons
				fundingSource={"paypal"}
				className="dark:!bg-transparent -mb-1.5 min-w-44"
				style={{
					borderRadius: 8,
					color: "gold",
					layout: "horizontal",
					height: 45,
				}}
				disabled={isPaymentSubmitting}
				onError={() => {
					setPaymentSubmitting(false);
					const message =
						"PayPal couldn't complete this payment. Try again, or pay with a card.";
					toast.error(message, {
						duration: 5000,
					});
					captureBecomeAMemberEvent("payment_failed", {
						...capturePlanProperties,
						error_code: "paypal_error",
						error_message: message,
					});
				}}
				createSubscription={(_, actions) => {
					setPaymentSubmitting(true);
					if (surface === "modal") {
						captureBecomeAMemberEvent(
							"checkout_started",
							capturePlanProperties,
						);
					}
					return isChangingPlan
						? actions.subscription.revise(paypalSubscription.id, {
								plan_id: planConfig.price?.paypalPlanId!,
							})
						: actions.subscription.create({
								plan_id: planConfig.price?.paypalPlanId!,
								custom_id: userId!,
							});
				}}
				onApprove={async (data) => {
					const newSubscription = {
						userId: userId!,

						isPaused: false,
						paymentProvider: PaymentProvider.PAYPAL,
						id: data.subscriptionID!,
						status: SubscriptionStatus.ACTIVE,
						planType: planConfig.planType,
						createdAt: new Date(),
						updatedAt: new Date(),
					} as SubscriptionExpanded;

					// Success UI first — then cache membership for gates.
					completeCheckout("paypal");

					await refreshSubscriptionDB(newSubscription, {
						revalidate: false,
					});

					await globalMutate("/onboarding/checklist");
				}}
			/>
		</PayPalScriptProvider>
	);
};

export default memo(BecomeAMemberCheckoutPayPalButton);
